import { useContext, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { getOptimiserSettings, getUserDetails } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { RippleLoaderLines } from '../../ui';
import AccountInformation from '../../ui/Dashboard/Settings/AccountInformation';
import AdditionalUserInformation from '../../ui/Dashboard/Settings/AdditionalUserInformation';
import InformationPanel from '../../ui/Dashboard/Settings/InformationPanel';
import InverterConnectionSettings from '../../ui/Dashboard/Settings/InverterConnectionSettings';
import MinimumSOCSettings from '../../ui/Dashboard/Settings/MinimumSOCSettings';
import OnOffSettings from '../../ui/Dashboard/Settings/OnOffSettings';
import SelfConsumptionModeSettings from '../../ui/Dashboard/Settings/SelfConsumptionModeSettings';
import SignOutSettings from '../../ui/Dashboard/Settings/SignOutSettings';
import SubscriptionDetails from '../../ui/Dashboard/Settings/SubscriptionDetails';
import TariffInformation from '../../ui/Dashboard/Settings/TariffInformation';
import TDASInformation from '../../ui/Dashboard/Settings/TDASInformation';
import InfoModal from '../../ui/Modal/InfoModal';

const StyledSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const SettingsContainer = () => {
  const {
    setUserDetails,
    setOptimiserSettings,
    userSub,
    isAdmin,
    userMpan,
    tdasIsActive,
    userDetails,
    subscriptionsDisabled,
  } = useContext(AppContext);
  const [showError, setShowError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const loadUser = () => {
    void getUserDetails(userSub).then((res) => {
      setUserDetails(res);
      setLoading(false);
    });
  };

  const loadOptimiserSettings = () => {
    void getOptimiserSettings(userSub).then((res) => {
      setOptimiserSettings(res);
    });
  };

  useEffect(() => {
    loadUser();
    loadOptimiserSettings();
  }, []);

  return (
    <StyledSettingsContainer>
      <InfoModal
        show={showError}
        onClose={() => setShowError(false)}
        infoContent={
          <div>
            Something went wrong and we weren't able to save your changes to the optimiser. Please try again and if the
            problem persists, please{' '}
            <a target="_blank" href="https://kb.loop.homes/kb-tickets/new" rel="noreferrer">
              contact support
            </a>
            .
          </div>
        }
      />
      {tdasIsActive && !userMpan && <TDASInformation />}
      {isAdmin && <AdditionalUserInformation />}
      {!isAdmin && <AccountInformation />}
      {!subscriptionsDisabled && <SubscriptionDetails />}
      <TariffInformation />
      <InverterConnectionSettings />
      {userDetails?.has_smart_tariff && (
        <>
          <MinimumSOCSettings loadUser={loadUser} setShowError={setShowError} />
          <OnOffSettings loadUser={loadUser} setShowError={setShowError} />
          <SelfConsumptionModeSettings loadOptimiserSettings={loadOptimiserSettings} setShowError={setShowError} />
        </>
      )}
      {loading && (
        <InformationPanel title="">
          <RippleLoaderLines lines={4} className="loading-lines" />
        </InformationPanel>
      )}
      <SignOutSettings />
    </StyledSettingsContainer>
  );
};

export default function WrappedSettingsContainer() {
  const theme = useTheme();
  return withPageTitle(`${theme.language.appName} Settings`, SettingsContainer)();
}
